import { Heading, Button } from '@simplywallst/ui-core/next';

import Loader from '@assets/images/portfolio/loader.svg';

import CrossSolid from '@simplywallst/ui-core/icons/CrossSolid';
import SuccessSolid from '@simplywallst/ui-core/icons/SuccessSolid';
import DividendOutline from '@simplywallst/ui-core/icons/DividendOutline';
import StockSplitOutline from '@simplywallst/ui-core/icons/StockSplitOutline';
import ChartUpOutline from '@simplywallst/ui-core/icons/ChartUpOutline';

import WarningSolid from '@simplywallst/ui-core/icons/WarningSolid';

import { pluralise } from '@/utilities/pluralise';

type Props = {
  selectedFileName: string;
  populatedRowCount: number;
  incompleteRowCount: number;
  onClose: () => void;
  matchingStatus: 'pending' | 'completed';
};

export const SuccessState = ({
  onClose,
  selectedFileName,
  populatedRowCount,
  incompleteRowCount,
  matchingStatus,
}: Props) => {
  return (
    <div className="relative rounded-md bg-p-white px-6 py-x4">
      <Button
        variant="subtle"
        onClick={onClose}
        aria-label="Close"
        className="absolute right-4 top-2 p-0"
      >
        <CrossSolid fill="spacegray" />
      </Button>
      <Heading
        as="h2"
        size="xLarge"
        className="flex items-center gap-x1 text-pretty text-p-grey-100"
      >
        Your file was successfully imported!
        <SuccessSolid className="size-7 fill-good" />
      </Heading>
      <div className="mt-x1 text-sm text-soft">{`${selectedFileName}`}</div>
      <div className="mt-5 flex flex-col justify-center rounded-lg bg-surface-3 p-x3">
        <p className="m-0 text-sm font-strong text-soft">
          Total transactions imported
        </p>
        {matchingStatus === 'pending' ? (
          <Loader className="size-x4 animate-spin" />
        ) : (
          <div className="text-[36px]" aria-label="Total imported transaction">
            {populatedRowCount}{' '}
          </div>
        )}
        {incompleteRowCount > 0 && (
          <div
            className="mt-x2 flex items-center text-sm"
            aria-label="Incomplete Row Count"
          >
            <WarningSolid className="mr-1 fill-warning" />
            <div>
              <span className="font-strong">
                {incompleteRowCount}{' '}
                {pluralise(incompleteRowCount, 'transaction', 'transactions')}
              </span>
              &nbsp;{pluralise(incompleteRowCount, 'needs', 'need')} your
              attention
            </div>
          </div>
        )}
      </div>
      <div className="mt-x4 text-sm font-strong">
        💡 When reviewing, remember...
      </div>

      <div className="mt-x2 grid grid-cols-[auto] gap-6 md:grid-cols-3">
        <div className="grid gap-2 rounded-md bg-surface-3 px-x1_5 py-x2">
          <DividendOutline className="w-x3 fill-legacy-spacegray" />
          <Heading as="h3" weight="strong" size="xSmall">
            Dividends Handled Automatically
          </Heading>
          <div className="text-xs text-soft">
            No need to add dividends or corporate events. They&apos;re
            automatically accounted for.
          </div>
        </div>
        <div className="grid gap-2 rounded-md bg-surface-3 px-x1_5 py-x2">
          <StockSplitOutline className="w-x3 fill-legacy-spacegray" />
          <Heading as="h3" weight="strong" size="xSmall">
            Stock Splits? We&apos;ve Got You Covered
          </Heading>
          <div className="text-xs text-soft">
            Enter your transactions with the original stock price and units.
            Stock splits will be adjusted automatically.
          </div>
        </div>
        <div className="grid gap-2 rounded-md bg-surface-3 px-x1_5 py-x2">
          <ChartUpOutline className="w-x3 fill-legacy-spacegray" />
          <Heading as="h3" weight="strong" size="xSmall">
            Double-check stock tickers and exchanges
          </Heading>
          <div className="text-xs text-soft">
            Ensure each holding is matched with the correct ticker and exchange
            for accurate tracking.
          </div>
        </div>
      </div>
      <div className="mt-x6 flex justify-end">
        <Button
          variant="primary"
          isDisabled={matchingStatus === 'pending'}
          onClick={onClose}
        >
          Continue to review
        </Button>
      </div>
    </div>
  );
};
